import { useState, useEffect, forwardRef, useImperativeHandle, useRef, useCallback } from 'react';
import Button from '@/components/atoms/Button/Button';
import { Toast } from '@/components/atoms';
import GradingFlashCard from '@/components/organisms/Grading/FlashCard';
import GradingDragMatch from '@/components/organisms/Grading/DragMatch';
import GradingSpellCheck from '@/components/organisms/Grading/SpellCheck';
import GradingWordChoice from '@/components/organisms/Grading/WordChoice';
import GradingDiagram from '@/components/organisms/Grading/Diagram';
import GradingPartWriting from '@/components/organisms/Grading/PartWriting';
import GradingDictation from '@/components/organisms/Grading/Dictation';
import GradingFillBlank from '@/components/organisms/Grading/FillBlank';
import GradingEngWritng from '@/components/organisms/Grading/EngWritng';
import GradingFreeWritng from '@/components/organisms/Grading/FreeWritng';
import GradingStoryDialog from '@/components/organisms/Grading/StoryDialog';
import GradingStoryDialogQustion from '@/components/organisms/Grading/StoryDialogQustion';
import GradingVideoQuiz from '@/components/organisms/Grading/VideoQuiz';
import GradingAlphabetChoice from '@/components/organisms/Grading/AlphabetChoice';
import { academyLearningScheduleApi } from 'services/api';

export type GradingResultSummary = {
  totalItems: number;
  correct: number;
  incorrect: number;
  grading: number;
  score: number;
};

interface HomeworkScoreProps {
  type?: 'score' | 'grading'
  data?: any
  platform?: 'admin' | 'academy' | 'student' | 'public'
  hideVideoQuiz?: boolean
  suppressVideoQuizUi?: boolean
  excludeVideoQuizFromSummary?: boolean
  onGradingResultChange?: (result: GradingResultSummary | null) => void
  onStatusChange?: (status: string) => void
}
const HomeworkScore = forwardRef(({
  type = 'score',
  data,
  platform = 'student',
  hideVideoQuiz = false,
  suppressVideoQuizUi = false,
  excludeVideoQuizFromSummary = false,
  onGradingResultChange,
  onStatusChange,
}: HomeworkScoreProps, ref) => {
  const resolvedPlanValue = (() => {
    if (data?.academyPlanType) return String(data.academyPlanType).toLowerCase();
    if (platform === 'academy') return 'standard';
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('academy_current_plan');
      return stored ? stored.toLowerCase() : '';
    }
    return '';
  })();
  const canUseAi = platform === 'admin' || (platform === 'academy' && resolvedPlanValue === 'plus');
  // 일괄 AI 채점 진행 (DOM 버튼 클릭 방식으로 단순화)
  const runBulkAiGrading = async () => {
    if (!canUseAi) {
      alert('AI 채점은 플러스 요금제에서만 제공됩니다.');
      return;
    }
    try {
      // 현재 DOM에서 "AI 채점" 텍스트를 포함하는 모든 버튼 찾기
      const allButtons = document.querySelectorAll('button');
      const aiGradingButtons: HTMLButtonElement[] = [];

      allButtons.forEach((btn) => {
        const text = btn.textContent?.trim() || '';
        // "AI 채점하기" 또는 "AI 채점" 텍스트를 포함하는 버튼만 수집
        if (text.includes('AI 채점')) {
          aiGradingButtons.push(btn as HTMLButtonElement);
        }
      });

      if (aiGradingButtons.length === 0) {
        alert('AI 채점 가능한 항목이 없습니다.');
        return;
      }

      // 각 버튼을 순차적으로 클릭 (disabled 상태가 아닌 것만)
      let clickedCount = 0;
      for (const btn of aiGradingButtons) {
        if (!btn.disabled) {
          btn.click();
          clickedCount++;
          // 각 클릭 사이에 500ms 대기 (API 호출 및 상태 업데이트 시간 확보)
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }

      if (clickedCount === 0) {
        alert('AI 채점 가능한 항목이 없습니다. (모든 버튼이 비활성화 상태)');
      } else {
        alert(`AI 일괄 채점이 완료되었습니다. (${clickedCount}개 항목 처리)`);
      }
    } catch (e) {
      console.error('AI 일괄 채점 오류:', e);
      alert('AI 일괄 채점 중 오류가 발생했습니다.');
    }
  }

  useImperativeHandle(ref, () => ({ runBulkAiGrading }));
  const [isGradingCompleted, setIsGradingCompleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lockedManualKeys, setLockedManualKeys] = useState<Set<string>>(new Set());
  const [lockedRecordingKeys, setLockedRecordingKeys] = useState<Set<string>>(new Set());
  const [lockedAccuracyKeys, setLockedAccuracyKeys] = useState<Set<string>>(new Set());

  const isManualLocked = useCallback((key?: string | null) => {
    if (!key) return false;
    return lockedManualKeys.has(key);
  }, [lockedManualKeys]);

  const isRecordingLocked = useCallback((key?: string | null) => {
    if (!key) return false;
    return lockedRecordingKeys.has(key);
  }, [lockedRecordingKeys]);

  const [toast, setToast] = useState<{ message: string; status: 'success' | 'error' | 'info' } | null>(null);
  // 녹음 채점 결과를 저장하는 상태 (activityId를 키로 사용)
  const [recordingGradingResults, setRecordingGradingResults] = useState<Record<string, 'correct' | 'incorrect'>>({});
  // 수동 채점 결과를 저장하는 상태 (활동별로 관리)
  const [manualGradingResults, setManualGradingResults] = useState<Record<string, any>>({});
  const [writingAccuracies, setWritingAccuracies] = useState<Record<string, number | null>>({});
  const [teacherComments, setTeacherComments] = useState<Record<string, string>>({});

  // 저장된 채점 결과 복원
  useEffect(() => {
    if (!data) {
      setRecordingGradingResults({});
      setManualGradingResults({});
        setWritingAccuracies({});
        setTeacherComments({});
      setLockedManualKeys(new Set());
      setLockedRecordingKeys(new Set());
      setLockedAccuracyKeys(new Set());
      setIsGradingCompleted(false);
      return;
    }

    const savedResults = data.savedGradingResult || {};
    const savedRecordings = (savedResults.recordingGradingResults as Record<string, 'correct' | 'incorrect'>) || {};
    const savedManuals = (savedResults.manualGradingResults as Record<string, any>) || {};
    const savedAccuracies = (savedResults.writingAccuracies as Record<string, number | null>) || {};
    const savedComments = (savedResults.teacherComments as Record<string, string>) || {};

    setRecordingGradingResults(savedRecordings);
    setManualGradingResults(savedManuals);
    setWritingAccuracies(savedAccuracies);
    setTeacherComments(savedComments);

    setLockedRecordingKeys(new Set(Object.keys(savedRecordings)));
    setLockedManualKeys(new Set(Object.keys(savedManuals)));
    setLockedAccuracyKeys(new Set(Object.keys(savedAccuracies)));

    setIsGradingCompleted(data.status === '채점완료');
  }, [data]);

  // 전역 scheduleId 주입 (AI 채점 호출에서 사용)
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        (window as any).__CURRENT_SCHEDULE_ID__ = data?.scheduleId;
      }
    } catch {}
  }, [data?.scheduleId]);

  // 아이템을 펼치지 않고 전체를 하나로 처리하는 활동 타입들 (학습 화면과 동일)
  const SINGLE_ITEM_ACTIVITIES = [
    'DRAG_MATCH',
    'SPELL_CHECK', 
    'ALPHABET_CHOICE',
    'PART_WRITING',
    'STORY_DIALOG',
    'STORY_DIALOG_QA',
    'VIDEO_QUIZ',
    'DIAGRAM',
    'DICTATION',
    'FILL_BLANK',
    'ENG_WRITING',
    'FREE_WRITING'
  ];

const buildWordChoiceRecordingKey = (activityId: any, itemId?: any, itemIndex?: number | null) => {
  const baseId = activityId ?? 'unknown';
  const normalizedItemId = itemId ?? (typeof itemIndex === 'number' ? itemIndex : 0);
  return `wc-${baseId}-${normalizedItemId}`;
};

type ItemStatus = 'correct' | 'incorrect' | 'grading';

const mapManualStatus = (value?: 'correct' | 'incorrect' | null): ItemStatus => {
  if (value === 'correct') return 'correct';
  if (value === 'incorrect') return 'incorrect';
  return 'grading';
};

const mapRecordingStatus = (value?: 'correct' | 'incorrect' | null): ItemStatus => {
  if (value === 'correct') return 'correct';
  if (value === 'incorrect') return 'incorrect';
  return 'grading';
};

const mapBooleanStatus = (value: boolean | undefined | null, fallback: ItemStatus = 'grading'): ItemStatus => {
  if (value === true) return 'correct';
  if (value === false) return 'incorrect';
  return fallback;
};

const mergeStatuses = (statuses: ItemStatus[]): ItemStatus => {
  if (!statuses.length) return 'grading';
  if (statuses.some((status) => status === 'incorrect')) return 'incorrect';
  if (statuses.every((status) => status === 'correct')) return 'correct';
  return 'grading';
};

const normalizeWordChoiceText = (value?: string | null) => {
  if (typeof value !== 'string') return '';
  return value.replace(/[^a-z0-9\s]/gi, '').trim().toLowerCase();
};

const parseJsonSafely = (raw: any) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

const evaluateWordChoiceWriting = (activity: any, itemResponse: any, itemIdx: number): boolean | null => {
  if (!activity?.items || activity.items.length === 0) return null;
  const targetItem = activity.items[itemIdx] || activity.items[0];
  const answerJson = parseJsonSafely(targetItem?.answerJson);
  const contentJson = parseJsonSafely(targetItem?.contentJson);
  const correctText = String(
    answerJson.correctSentence ??
    contentJson.sentence ??
    contentJson.word ??
    ''
  ).trim();
  if (!correctText) return null;

  const studentAnswer = (() => {
    if (!itemResponse) return '';
    if (typeof itemResponse.answer === 'string' && itemResponse.answer.trim()) {
      return itemResponse.answer;
    }
    if (typeof itemResponse.userInput === 'string' && itemResponse.userInput.trim()) {
      return itemResponse.userInput;
    }
    if (Array.isArray(itemResponse.selectedWords) && itemResponse.selectedWords.length > 0) {
      return itemResponse.selectedWords.join(' ');
    }
    return '';
  })();

  if (!studentAnswer) return null;

  return normalizeWordChoiceText(studentAnswer) === normalizeWordChoiceText(correctText);
};

const getFillBlankExpectedText = (activity: any): string => {
  let content: any = {};
  try {
    const firstItem = activity?.items?.[0];
    if (firstItem?.contentJson) {
      content = typeof firstItem.contentJson === 'string'
        ? JSON.parse(firstItem.contentJson)
        : firstItem.contentJson;
    }
  } catch (_) {
    content = {};
  }

  const sentence: string = typeof content.sentence === 'string'
    ? content.sentence
    : typeof content.text === 'string'
      ? content.text
      : '';
  const blankSource = content.blankWords ?? content.blank ?? '';
  const blankBase: string = typeof blankSource === 'string' ? blankSource : '';

  if (!sentence || !blankBase) return blankBase;
  const blankIndex = sentence.indexOf(blankBase);
  if (blankIndex < 0) return blankBase;

  const suffix = sentence.slice(blankIndex + blankBase.length);
  const dotMatch = suffix.match(/^\.+/);
  if (!dotMatch || blankBase.endsWith('.')) return blankBase;

  return `${blankBase}${dotMatch[0]}`;
};

const getFillBlankStudentAnswer = (activityResponse: any): string => {
  if (!activityResponse) return '';
  if (typeof activityResponse.inputText === 'string') return activityResponse.inputText;
  if (typeof activityResponse.answer === 'string') return activityResponse.answer;
  if (typeof activityResponse.userInput === 'string') return activityResponse.userInput;
  if (typeof activityResponse.input === 'string') return activityResponse.input;
  return '';
};

const normalizeVideoQuizSelection = (value: any): number[] => {
  const bucket: number[] = [];
  if (Array.isArray(value)) {
    value.forEach((entry) => {
      const numeric = typeof entry === 'number' ? entry : Number(entry);
      if (Number.isFinite(numeric)) {
        bucket.push(Math.trunc(numeric));
      }
    });
  } else if (typeof value === 'number' && Number.isFinite(value)) {
    bucket.push(Math.trunc(value));
  } else if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      bucket.push(Math.trunc(parsed));
    }
  }
  const unique = Array.from(new Set(bucket));
  unique.sort((a, b) => a - b);
  return unique;
};

const normalizeVideoQuizCorrectAnswers = (answer: any): number[] =>
  normalizeVideoQuizSelection(answer).map((val) => val + 1);

const videoSelectionsEqual = (student: number[], correct: number[]): boolean => {
  if (student.length === 0 || correct.length === 0) {
    return false;
  }
  if (student.length !== correct.length) {
    return false;
  }
  return student.every((value, idx) => value === correct[idx]);
};

const collectFlatItemStatuses = (
  activity: any,
  flatItem: any,
  activityResponse: any,
  recordingGradingResults: Record<string, 'correct' | 'incorrect'>,
  manualGradingResults: Record<string, any>
): ItemStatus[] => {
  if (!activity) return ['grading'];
  const statuses: ItemStatus[] = [];
  const activityType = activity.activityType;
  const itemIdx = typeof flatItem?.itemIndex === 'number' ? flatItem.itemIndex : 0;
  const itemCount = Array.isArray(activity.items) ? activity.items.length : 0;

  const ensureStatuses = (): ItemStatus[] => (statuses.length ? statuses : (['grading'] as ItemStatus[]));

  if (activityType === 'VIDEO_QUIZ') {
    try {
      const config = typeof activity.configJson === 'string'
        ? JSON.parse(activity.configJson)
        : activity.configJson || {};
      const quizzes: any[] = Array.isArray(config?.quizzes) ? config.quizzes : [];
      if (quizzes.length === 0) return ensureStatuses();
      quizzes.forEach((quiz: any, idx: number) => {
        if (activityResponse && activityResponse.selectedAnswers) {
          const studentAnswer = normalizeVideoQuizSelection(activityResponse.selectedAnswers[idx + 1]);
          const correctAnswers = normalizeVideoQuizCorrectAnswers(quiz.answer);
          if (studentAnswer.length === 0) {
            statuses.push('grading');
          } else {
            statuses.push(videoSelectionsEqual(studentAnswer, correctAnswers) ? 'correct' : 'incorrect');
          }
        } else {
          statuses.push('grading');
        }
      });
    } catch {
      statuses.push('grading');
    }
    return ensureStatuses();
  }

  const getManualOrAutoStatus = (manualKey: string, fallbackKey?: string): ItemStatus => {
    const manualValue = manualGradingResults[manualKey] ?? (fallbackKey ? manualGradingResults[fallbackKey] : undefined);
    if (manualValue !== undefined) {
      return mapManualStatus(manualValue);
    }
    if (activityResponse && typeof activityResponse.isCorrect === 'boolean') {
      return mapBooleanStatus(activityResponse.isCorrect);
    }
    return 'grading';
  };

  if (!SINGLE_ITEM_ACTIVITIES.includes(activityType) && activity.items && activity.items.length > 0) {
    if (activityType === 'WORD_CHOICE') {
      const recordingKey = buildWordChoiceRecordingKey(activity.id, flatItem?.item?.id, itemIdx);
      const legacyRecordingKey = `wc-${activity.id}`;
      const hasRecording = !!(activityResponse && activityResponse.recordingUrl);
      if (hasRecording) {
        const recordingStatus = recordingGradingResults[recordingKey]
          ?? (itemCount <= 1 ? recordingGradingResults[legacyRecordingKey] : undefined);
        statuses.push(mapRecordingStatus(recordingStatus));
      }

      const hasWrittenAnswer = !!(activityResponse && (
        (typeof activityResponse.answer === 'string' && activityResponse.answer.trim()) ||
        (typeof activityResponse.userInput === 'string' && activityResponse.userInput.trim()) ||
        (Array.isArray(activityResponse.selectedWords) && activityResponse.selectedWords.length > 0)
      ));

      if (hasWrittenAnswer) {
        const manualKey = `${activity.id}-${itemIdx}`;
        const fallbackKey = `${activity.id}-0`;
        const manualValue = manualGradingResults[manualKey] ?? manualGradingResults[fallbackKey];
        if (manualValue !== undefined) {
          statuses.push(mapManualStatus(manualValue));
        } else {
          const autoResult = evaluateWordChoiceWriting(activity, activityResponse, itemIdx);
          if (autoResult === true) statuses.push('correct');
          else if (autoResult === false) statuses.push('incorrect');
          else statuses.push('grading');
        }
      }

      return ensureStatuses();
    }

    if (activityType === 'FLASH_CARD') {
      const recordingKey = `flashcard-${activity.id}-${itemIdx}`;
      statuses.push(mapRecordingStatus(recordingGradingResults[recordingKey]));
      return ensureStatuses();
    }

    const manualKey = `${activity.id}-${itemIdx}`;
    if (manualGradingResults[manualKey] !== undefined) {
      statuses.push(mapManualStatus(manualGradingResults[manualKey]));
    } else if (activityResponse && typeof activityResponse.isCorrect === 'boolean') {
      statuses.push(mapBooleanStatus(activityResponse.isCorrect));
    } else {
      statuses.push('grading');
    }
    return ensureStatuses();
  }

  switch (activityType) {
    case 'STORY_DIALOG':
    case 'STORY_DIALOG_QA': {
      const itemCount = activity.items?.length || 0;
      for (let i = 0; i < itemCount; i++) {
        const legacyKey = activityType === 'STORY_DIALOG'
          ? `${activity.id}-sentence-${i}`
          : `${activity.id}-question-${i}`;
        const stableKey = activityType === 'STORY_DIALOG'
          ? `sd-${activity.id}-${i}`
          : `sdq-${activity.id}-${i}`;
        const manualValue = manualGradingResults[stableKey] ?? manualGradingResults[legacyKey];
        statuses.push(mapManualStatus(manualValue));
      }
      return ensureStatuses();
    }
    case 'DICTATION': {
      const typedStatus = (() => {
        if (activityResponse && typeof activityResponse.isCorrect === 'boolean') {
          return mapBooleanStatus(activityResponse.isCorrect, 'incorrect');
        }
        // 기존 로직과 동일하게 응답이 없으면 오답 처리
        return 'incorrect';
      })();
      statuses.push(typedStatus);

      const recordingKey = `dictation-${activity.originalItemId || activity.items?.[0]?.id || activity.id}`;
      statuses.push(mapRecordingStatus(recordingGradingResults[recordingKey]));
      return ensureStatuses();
    }
    case 'FILL_BLANK': {
      const studentAnswer = getFillBlankStudentAnswer(activityResponse);
      if (!studentAnswer) {
        statuses.push('grading');
        return ensureStatuses();
      }

      const expected = getFillBlankExpectedText(activity);
      if (!expected) {
        statuses.push(mapBooleanStatus(activityResponse?.isCorrect, 'grading'));
        return ensureStatuses();
      }

      statuses.push(studentAnswer === expected ? 'correct' : 'incorrect');
      return ensureStatuses();
    }
    case 'PART_WRITING': {
      const stableBaseId = activity?.originalType === 'WORD_CHOICE'
        ? (activity.items?.[0]?.refId ?? activity.id)
        : activity.id;
      let recordingEnabled = false;
      try {
        const cfg = typeof activity.configJson === 'string'
          ? JSON.parse(activity.configJson)
          : activity.configJson || {};
        recordingEnabled = !!cfg?.recordingEnabled;
      } catch (_) {}

      const hasRecordingResponse = Boolean(activityResponse && activityResponse.recordingUrl);
      if (hasRecordingResponse) {
        const recordingKey = `partwriting-${stableBaseId}`;
        statuses.push(mapRecordingStatus(recordingGradingResults[recordingKey]));
      }

      const manualKey = `pw-${stableBaseId}`;
      const manualVal = manualGradingResults[manualKey];

      if (manualVal !== undefined) {
        statuses.push(mapManualStatus(manualVal));
        return ensureStatuses();
      }

      // 녹음이 없는 문제는 자동 판정 값(isCorrect)만 사용하고 수동 채점 표시를 숨김
      if (!recordingEnabled && !hasRecordingResponse) {
        if (activityResponse && typeof activityResponse.isCorrect === 'boolean') {
          statuses.push(mapBooleanStatus(activityResponse.isCorrect));
        }
        return ensureStatuses();
      }

      // 녹음 활성 문제이지만 수동 채점이 안 된 경우
      statuses.push('grading');
      return ensureStatuses();
    }
    case 'DIAGRAM': {
      try {
        const config = typeof activity.configJson === 'string'
          ? JSON.parse(activity.configJson)
          : activity.configJson || {};
        const categories: string[] = Array.isArray(config?.categories) ? config.categories : [];
        const correctWords: string[][] = Array.isArray(config?.words) ? config.words : [];
        const resp = activityResponse || {};
        const wordLocations: Record<string, string[]> = resp?.wordLocations || {};

        if (categories.length === 0) {
          statuses.push(activityResponse && typeof activityResponse.isCorrect === 'boolean'
            ? mapBooleanStatus(activityResponse.isCorrect, 'incorrect')
            : 'incorrect');
          return ensureStatuses();
        }

        categories.forEach((categoryName: string, catIdx: number) => {
          const correctArr = (correctWords[catIdx] || []).map((w: string) => String(w).toLowerCase());
          const studentArr = (wordLocations[categoryName] || []).map((w: string) => String(w).toLowerCase());
          const correctSet = new Set(correctArr);
          const studentSet = new Set(studentArr);
          const sameSize = studentSet.size === correctSet.size;
          const allIncluded = correctArr.every((w: string) => studentSet.has(w));
          statuses.push(sameSize && allIncluded ? 'correct' : 'incorrect');
        });
      } catch {
        statuses.push(activityResponse && typeof activityResponse.isCorrect === 'boolean'
          ? mapBooleanStatus(activityResponse.isCorrect, 'incorrect')
          : 'incorrect');
      }
      return ensureStatuses();
    }
    case 'ENG_WRITING': {
      const manualKey = `${activity.id}`;
      statuses.push(mapManualStatus(manualGradingResults[manualKey]));
      return ensureStatuses();
    }
    case 'FREE_WRITING': {
      try {
        const resp = activityResponse || {};
        const inputTexts = Array.isArray(resp.inputTexts) ? resp.inputTexts : [];
        if (inputTexts.length === 0) {
          statuses.push('grading');
        } else {
          inputTexts.forEach((_: string, idx: number) => {
            const lineKey = `fw-${activity.id}-${idx}`;
            statuses.push(mapManualStatus(manualGradingResults[lineKey]));
          });
        }
      } catch {
        statuses.push('grading');
      }
      return ensureStatuses();
    }
    case 'DRAG_MATCH': {
      const resp = activityResponse || {};
      if (Array.isArray(resp.matchedPairs) && resp.matchedPairs.length > 0) {
        resp.matchedPairs.forEach((pair: any) => {
          statuses.push(mapBooleanStatus(pair?.isCorrect, 'incorrect'));
        });
      } else if (typeof resp.isCorrect === 'boolean') {
        statuses.push(mapBooleanStatus(resp.isCorrect, 'incorrect'));
      } else {
        statuses.push('grading');
      }
      return ensureStatuses();
    }
    default: {
      const manualKey = `${activity.id}`;
      if (manualGradingResults[manualKey] !== undefined) {
        statuses.push(mapManualStatus(manualGradingResults[manualKey]));
      } else if (activityResponse && typeof activityResponse.isCorrect === 'boolean') {
        statuses.push(mapBooleanStatus(activityResponse.isCorrect));
      } else {
        statuses.push('grading');
      }
      return ensureStatuses();
    }
  }
};

  // Unit Type별 학습 활동 구성 정의 (학습 화면과 완전히 동일)
  const UNIT_TYPE_CONFIG: Record<string, {
    allowedDbActivities: string[];
    flashCardExtensions?: string[];
    wordChoiceExtensions?: string[];
    dragMatchMaxItems?: number | null;
  }> = {
    'PHONICS_DAY': {
      allowedDbActivities: ['FLASH_CARD', 'DICTATION'],
      flashCardExtensions: ['DRAG_MATCH', 'SPELL_CHECK', 'ALPHABET_CHOICE'],
      dragMatchMaxItems: null,
    },
    'STARTER_PHONICS': {
      allowedDbActivities: ['FLASH_CARD', 'DICTATION'],
      flashCardExtensions: ['DRAG_MATCH', 'SPELL_CHECK'],
      dragMatchMaxItems: null,
    },
    'STARTER_NUANCE': {
      allowedDbActivities: ['FLASH_CARD', 'WORD_CHOICE', 'DICTATION'],
      flashCardExtensions: ['DRAG_MATCH'],
      wordChoiceExtensions: ['PART_WRITING'],
      dragMatchMaxItems: 6,
    },
    'STARTER_STRUCTURE': {
      allowedDbActivities: ['FLASH_CARD', 'WORD_CHOICE', 'DIAGRAM', 'DICTATION'],
      flashCardExtensions: ['DRAG_MATCH'],
      wordChoiceExtensions: [],
      dragMatchMaxItems: null,
    },
    'TRANSLATION': {
      allowedDbActivities: ['FLASH_CARD', 'WORD_CHOICE', 'PART_WRITING', 'FREE_WRITING', 'DICTATION', 'ENG_WRITING'],
      flashCardExtensions: [],
      wordChoiceExtensions: [],
      dragMatchMaxItems: null,
    },
    'ESSAY': {
      allowedDbActivities: ['FLASH_CARD', 'PART_WRITING', 'FREE_WRITING', 'DICTATION', 'FILL_BLANK', 'ENG_WRITING'],
      flashCardExtensions: [],
      wordChoiceExtensions: [],
      dragMatchMaxItems: null,
    },
    'READING_BOOK': {
      allowedDbActivities: ['FLASH_CARD', 'STORY_DIALOG', 'STORY_DIALOG_QA'],
      flashCardExtensions: ['DRAG_MATCH'],
      dragMatchMaxItems: null,
    }
  };

  // 활동 확장 함수 (학습 화면과 완전히 동일)
  const expandActivities = (activities: any[], unitType: string) => {
    const config = UNIT_TYPE_CONFIG[unitType];
    if (!config) return activities;


    // 1. DB 활동 필터링
    const filteredActivities = activities.filter(activity => 
      config.allowedDbActivities.includes(activity.activityType)
    );

    // 2. 활동 확장 및 변환
    const expandedActivities: any[] = [];

    filteredActivities.forEach((activity) => {
      // ESSAY/TRANSLATION: FLASH_CARD → DICTATION (각 item마다) + DRAG_MATCH (전체)
      if ((unitType === 'ESSAY' || unitType === 'TRANSLATION') && activity.activityType === 'FLASH_CARD') {
        
        // 각 item을 개별 DICTATION으로 변환
        activity.items.forEach((flashCardItem: any, idx: number) => {
          expandedActivities.push({
            ...activity,
            id: activity.id + Math.random(),
            activityType: 'DICTATION',
            title: '딕테이션',
            items: [{
              id: flashCardItem.id,
              itemType: 'DICTATION',
              orderIndex: idx,
              refType: 'FLASH_CARD',
              refId: flashCardItem.id,
              contentJson: flashCardItem.contentJson,
              answerJson: null,
              score: null
            }],
            isExtended: true,
            originalType: 'FLASH_CARD',
            originalItemId: flashCardItem.id
          });
        });

        // 전체 items를 하나의 DRAG_MATCH로 변환
        expandedActivities.push({
          ...activity,
          id: activity.id + 1000,
          activityType: 'DRAG_MATCH',
          title: '드래그 매치',
          items: [{
            id: activity.id,
            itemType: 'DRAG_MATCH',
            orderIndex: 0,
            refType: 'FLASH_CARD',
            refId: activity.id,
            contentJson: JSON.stringify({ flashCards: activity.items }),
            answerJson: null,
            score: null
          }],
          isExtended: true,
          originalType: 'FLASH_CARD'
        });

        return; // FLASH_CARD 자체는 추가하지 않음
      }

      // DICTATION에서 videoQuizEnabled/videoUrl 체크 (학습 화면과 동일)
      if (activity.activityType === 'DICTATION') {
        try {
          const configData = typeof activity.configJson === 'string'
            ? JSON.parse(activity.configJson)
            : (activity.configJson || {});
          const hasVideoUrl = !!configData.videoUrl;
          const isVideoQuiz = configData.videoQuizEnabled === true || hasVideoUrl;
          if (isVideoQuiz) {
            // VIDEO_QUIZ로 변환
            const videoQuizItems = activity.items && activity.items.length > 0 
              ? activity.items 
              : [{
                  id: activity.id,
                  itemType: 'VIDEO_QUIZ',
                  orderIndex: 0,
                  refType: 'NONE',
                  refId: null,
                  contentJson: activity.configJson,
                  answerJson: null,
                  score: null
                }];
            
            expandedActivities.push({
              ...activity,
              activityType: 'VIDEO_QUIZ',
              title: activity.title || '동영상 퀴즈',
              items: videoQuizItems
            });
            return;
          }
        } catch (e) {
          console.error('[확장] configJson 파싱 에러:', e);
        }
      }

      // STORY_DIALOG, STORY_DIALOG_QA는 그대로 추가
      if (activity.activityType === 'STORY_DIALOG' || activity.activityType === 'STORY_DIALOG_QA') {
        expandedActivities.push(activity);
        return;
      }

      // 기본 활동 추가
      expandedActivities.push(activity);

      // FLASH_CARD 확장 (ESSAY/TRANSLATION 제외)
      if (activity.activityType === 'FLASH_CARD' && config.flashCardExtensions && config.flashCardExtensions.length > 0) {
        
        config.flashCardExtensions.forEach((extensionType) => {
          if (extensionType === 'DRAG_MATCH') {
            // DRAG_MATCH: 전체를 하나로 묶음
            expandedActivities.push({
              ...activity,
              id: activity.id + 1000,
              activityType: extensionType,
              title: '드래그 매치',
              items: [{
                id: activity.id,
                itemType: extensionType,
                orderIndex: 0,
                refType: 'FLASH_CARD',
                refId: activity.id,
                contentJson: JSON.stringify({ flashCards: activity.items }),
                answerJson: null,
                score: null
              }],
              isExtended: true,
              originalType: 'FLASH_CARD'
            });
          } else if (extensionType === 'SPELL_CHECK' || extensionType === 'ALPHABET_CHOICE') {
            // SPELL_CHECK, ALPHABET_CHOICE: 각 item마다 개별 생성
            activity.items.forEach((flashCardItem: any, idx: number) => {
              expandedActivities.push({
                ...activity,
                id: activity.id + Math.random(),
                activityType: extensionType,
                title: extensionType === 'SPELL_CHECK' ? '스펠 체크' : '알파벳 고르기',
                items: [{
                  id: flashCardItem.id,
                  itemType: extensionType,
                  orderIndex: idx,
                  refType: 'FLASH_CARD',
                  refId: flashCardItem.id,
                  contentJson: flashCardItem.contentJson,
                  answerJson: null,
                  score: null
                }],
                isExtended: true,
                originalType: 'FLASH_CARD'
              });
            });
          }
        });
      }

      // WORD_CHOICE 확장
      if (activity.activityType === 'WORD_CHOICE' && config.wordChoiceExtensions && config.wordChoiceExtensions.length > 0) {
        
        config.wordChoiceExtensions.forEach((extensionType) => {
          if (extensionType === 'PART_WRITING' && activity.items?.length) {
            activity.items.forEach((wcItem: any, wcIdx: number) => {
              let perItemTitleTranslation = '';
              try {
                const wcContent = wcItem?.contentJson ? (typeof wcItem.contentJson === 'string' ? JSON.parse(wcItem.contentJson) : wcItem.contentJson) : {};
                perItemTitleTranslation = wcContent?.titleTranslation || '';
              } catch (e) {
                console.error('wcItem contentJson parse error:', e);
              }
              let parentConfigJson = activity.configJson;
              try {
                const parsedCfg = activity.configJson ? (typeof activity.configJson === 'string' ? JSON.parse(activity.configJson) : activity.configJson) : {};
                // item의 titleTranslation이 있으면 사용하고, 없으면 기존 config의 값(fallback) 사용
                parentConfigJson = JSON.stringify({ ...parsedCfg, titleTranslation: perItemTitleTranslation || parsedCfg.titleTranslation });
              } catch (e) {
                console.error('WORD_CHOICE parent config parse error:', e);
              }
              expandedActivities.push({
                ...activity,
                id: activity.id + Math.random(),
                activityType: extensionType,
                title: '부분 영작',
                items: [{
                  id: wcItem.id,
                  itemType: extensionType,
                  orderIndex: wcIdx,
                  refType: 'WORD_CHOICE',
                  refId: wcItem.id,
                  contentJson: JSON.stringify({ 
                    wordChoices: [wcItem],
                    parentConfigJson
                  }),
                  answerJson: null,
                  score: null
                }],
                isExtended: true,
                originalType: 'WORD_CHOICE'
              });
            });
            return;
          }
          expandedActivities.push({
            ...activity,
            id: activity.id + Math.random(),
            activityType: extensionType,
            title: extensionType === 'PART_WRITING' ? '부분 영작' :
                   extensionType === 'DIAGRAM' ? '다이어그램' :
                   extensionType === 'DICTATION' ? '받아쓰기' : activity.title,
            items: [{
              id: activity.id,
              itemType: extensionType,
              orderIndex: 0,
              refType: 'WORD_CHOICE',
              refId: activity.id,
              contentJson: JSON.stringify({ 
                wordChoices: activity.items,
                parentConfigJson: activity.configJson
              }),
              answerJson: null,
              score: null
            }],
            isExtended: true,
            originalType: 'WORD_CHOICE'
          });
        });
      }
    });

    // 2-3. ESSAY/TRANSLATION: PART_WRITING → ENG_WRITING 생성
    if ((unitType === 'ESSAY' || unitType === 'TRANSLATION') && config.allowedDbActivities.includes('ENG_WRITING')) {
      const partWritingActivities = expandedActivities.filter(act => act.activityType === 'PART_WRITING');
      
      partWritingActivities.forEach(activity => {
        expandedActivities.push({
          ...activity,
          id: activity.id + 1000,
          activityType: 'ENG_WRITING',
          title: '영작',
          items: [],
          isExtended: true,
          originalType: 'PART_WRITING'
        });
      });
    }

    return expandedActivities;
  };

  // 활동 순서 정렬 함수 (학습 화면과 동일)
  const sortActivities = (activities: any[], unitType: string) => {
    if (!activities || activities.length === 0) return activities;
    
    const sortedActivities = [...activities];
    
    if (unitType === 'ESSAY' || unitType === 'TRANSLATION') {
      // 에세이/번역: VIDEO_QUIZ, DICTATION, DRAG_MATCH, PART_WRITING, FILL_BLANK, ENG_WRITING, FREE_WRITING
      const activityOrder = ['VIDEO_QUIZ', 'DICTATION', 'DRAG_MATCH', 'PART_WRITING', 'FILL_BLANK', 'ENG_WRITING', 'FREE_WRITING'];
      
      sortedActivities.sort((a, b) => {
        const aIndex = activityOrder.indexOf(a.activityType);
        const bIndex = activityOrder.indexOf(b.activityType);
        
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        
        return aIndex - bIndex;
      });
    } else if (unitType === 'READING_BOOK') {
      // 리딩북: FLASH_CARD, DRAG_MATCH, STORY_DIALOG, STORY_DIALOG_QA
      const activityOrder = ['FLASH_CARD', 'DRAG_MATCH', 'STORY_DIALOG', 'STORY_DIALOG_QA'];
      
      sortedActivities.sort((a, b) => {
        const aIndex = activityOrder.indexOf(a.activityType);
        const bIndex = activityOrder.indexOf(b.activityType);
        
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        
        return aIndex - bIndex;
      });
    } else if (unitType === 'STARTER_NUANCE') {
      // 스타터 뉘앙스: VIDEO_QUIZ, FLASH_CARD, DRAG_MATCH, WORD_CHOICE, PART_WRITING
      const activityOrder = ['VIDEO_QUIZ', 'FLASH_CARD', 'DRAG_MATCH', 'WORD_CHOICE', 'PART_WRITING'];
      
      sortedActivities.sort((a, b) => {
        const aIndex = activityOrder.indexOf(a.activityType);
        const bIndex = activityOrder.indexOf(b.activityType);
        
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        
        return aIndex - bIndex;
      });
    } else if (unitType === 'STARTER_STRUCTURE') {
      // 스타터 스트럭처: VIDEO_QUIZ, FLASH_CARD, DRAG_MATCH, DIAGRAM, PART_WRITING
      const activityOrder = ['VIDEO_QUIZ', 'FLASH_CARD', 'DRAG_MATCH', 'DIAGRAM', 'PART_WRITING'];
      
      sortedActivities.sort((a, b) => {
        const aIndex = activityOrder.indexOf(a.activityType);
        const bIndex = activityOrder.indexOf(b.activityType);
        
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        
        return aIndex - bIndex;
      });
    } else {
      // 다른 유닛: VIDEO_QUIZ를 맨 앞으로
      const videoQuizIndex = sortedActivities.findIndex(act => act.activityType === 'VIDEO_QUIZ');
      
      if (videoQuizIndex > 0) {
        const videoQuiz = sortedActivities.splice(videoQuizIndex, 1)[0];
        sortedActivities.unshift(videoQuiz);
      }
    }
    
    return sortedActivities;
  };

  // flatItems 구조 생성 (학습 화면과 동일)
  const createFlatItems = (activities: any[]) => {
    const flatItems: any[] = [];
    
    
    activities.forEach((activity, activityIndex) => {
      if (hideVideoQuiz && activity.activityType === 'VIDEO_QUIZ') {
        return;
      }
      if (SINGLE_ITEM_ACTIVITIES.includes(activity.activityType)) {
        // 전체 활동을 하나의 아이템으로 처리
        flatItems.push({
          activity,
          item: activity.items && activity.items[0] ? activity.items[0] : {},
          globalIndex: flatItems.length,
          activityIndex,
          itemIndex: 0,
          isSingleItem: true
        });
      } else {
        // 아이템을 개별적으로 펼치는 활동들 (FLASH_CARD, WORD_CHOICE 등)
        if (activity.items && activity.items.length > 0) {
          activity.items.forEach((item: any, itemIndex: number) => {
            flatItems.push({
              activity,
              item,
              globalIndex: flatItems.length,
              activityIndex,
              itemIndex,
              isSingleItem: false
            });
          });
        } else {
          // items가 없는 경우에도 하나의 항목으로 추가
          flatItems.push({
            activity,
            item: {},
            globalIndex: flatItems.length,
            activityIndex,
            itemIndex: 0,
            isSingleItem: true
          });
        }
      }
    });
    
    return flatItems;
  };

  const scoreContainerRef = useRef<HTMLDivElement | null>(null);
  const domSyncTimeoutRef = useRef<number | null>(null);
  const summaryReadyRef = useRef(false);
  const [summaryReady, setSummaryReady] = useState(false);
  const [totalGradingResult, setTotalGradingResult] = useState<GradingResultSummary>({
    totalItems: 0,
    correct: 0,
    incorrect: 0,
    grading: 0,
    score: 0,
  });

  useEffect(() => {
    summaryReadyRef.current = false;
    setSummaryReady(false);
  }, [data?.scheduleId]);

  const markSummaryReady = useCallback(() => {
    if (!summaryReadyRef.current) {
      summaryReadyRef.current = true;
      setSummaryReady(true);
    }
  }, []);

  const computeTotalGradingResult = useCallback(() => {
    const textbooks = data?.textbooks || [];
    let correct = 0;
    let incorrect = 0;
    let grading = 0;

    textbooks.forEach((textbook: any) => {
      (textbook.units || []).forEach((unit: any) => {
        const expandedActivities = expandActivities(unit.activities || [], unit.unitType || '');
        const filteredActivities = hideVideoQuiz
          ? (expandedActivities || []).filter((activity: any) => activity.activityType !== 'VIDEO_QUIZ')
          : expandedActivities;
        const sortedActivities = sortActivities(filteredActivities, unit.unitType || '');
        const flatItems = createFlatItems(sortedActivities);

        let responses: Record<string, any> = {};
        if (unit.responseJson) {
          try {
            responses = JSON.parse(unit.responseJson);
          } catch (e) {
            console.error('responseJson 파싱 실패:', e);
          }
        }

        flatItems.forEach((flatItem: any) => {
          const activity = flatItem.activity;
          if (!activity) return;
          if (
            (type === 'grading' && activity.activityType === 'VIDEO_QUIZ') ||
            (hideVideoQuiz && activity.activityType === 'VIDEO_QUIZ') ||
            (excludeVideoQuizFromSummary && activity.activityType === 'VIDEO_QUIZ')
          ) {
            return;
          }
          const activityResponse = responses[flatItem.globalIndex] || {};
          const statuses = collectFlatItemStatuses(
            activity,
            flatItem,
            activityResponse,
            recordingGradingResults,
            manualGradingResults,
          );
          statuses.forEach((status) => {
            if (status === 'correct') correct += 1;
            else if (status === 'incorrect') incorrect += 1;
            else grading += 1;
          });
        });
      });
    });

    const totalItems = correct + incorrect + grading;
    const score = totalItems > 0 ? Math.round((correct / totalItems) * 100) : 0;
    return { totalItems, correct, incorrect, grading, score };
  }, [data, excludeVideoQuizFromSummary, hideVideoQuiz, manualGradingResults, recordingGradingResults, type]);

  useEffect(() => {
    const next = computeTotalGradingResult();
    setTotalGradingResult((prev) => {
      if (
        prev.totalItems === next.totalItems &&
        prev.correct === next.correct &&
        prev.incorrect === next.incorrect &&
        prev.grading === next.grading &&
        prev.score === next.score
      ) {
        return prev;
      }
      return next;
    });
  }, [computeTotalGradingResult]);

  useEffect(() => {
    if (!onGradingResultChange) return;
    if (!summaryReady) {
      onGradingResultChange(null);
      return;
    }
    onGradingResultChange(totalGradingResult);
  }, [summaryReady, totalGradingResult, onGradingResultChange]);

  useEffect(() => {
    // 새 채점 입력이 있을 때 즉시 요약을 "계산 중" 상태로 전환하여 잘못된 중간 값 노출을 피한다.
    summaryReadyRef.current = false;
    setSummaryReady(false);

    if (typeof window === 'undefined') return undefined;
    if (domSyncTimeoutRef.current) {
      window.clearTimeout(domSyncTimeoutRef.current);
    }
    domSyncTimeoutRef.current = window.setTimeout(() => {
      domSyncTimeoutRef.current = null;
      const container = scoreContainerRef.current;
      if (!container) return;
      const indicators = container.querySelectorAll<HTMLElement>('[data-grading-status]');
      if (!indicators.length) return;

      let correctCount = 0;
      let incorrectCount = 0;
      let gradingCount = 0;
      indicators.forEach((node) => {
        const status = node.getAttribute('data-grading-status');
        if (status === 'correct') correctCount += 1;
        else if (status === 'incorrect') incorrectCount += 1;
        else gradingCount += 1;
      });

      const totalItems = correctCount + incorrectCount + gradingCount;
      if (totalItems === 0) return;
      const score = Math.round((correctCount / totalItems) * 100);

      const nextSummary: GradingResultSummary = {
        totalItems,
        correct: correctCount,
        incorrect: incorrectCount,
        grading: gradingCount,
        score,
      };

      setTotalGradingResult((prev) => {
        if (
          prev.totalItems === nextSummary.totalItems &&
          prev.correct === nextSummary.correct &&
          prev.incorrect === nextSummary.incorrect &&
          prev.grading === nextSummary.grading &&
          prev.score === nextSummary.score
        ) {
          return prev;
        }
        return nextSummary;
      });
      markSummaryReady();
    }, 300); // 더 빠른 요약 반영을 위해 딜레이 단축

    return () => {
      if (domSyncTimeoutRef.current) {
        window.clearTimeout(domSyncTimeoutRef.current);
        domSyncTimeoutRef.current = null;
      }
    };
  }, [
    data,
    manualGradingResults,
    recordingGradingResults,
    hideVideoQuiz,
    suppressVideoQuizUi,
    excludeVideoQuizFromSummary,
    type,
    markSummaryReady,
  ]);

  const updateManualGradingResult = useCallback((key: string, status: 'correct' | 'incorrect' | 'grading' | undefined) => {
    if (!key) return;
    if (lockedManualKeys.has(key)) {
      return;
    }
    setManualGradingResults((prev) => {
      const next = { ...prev };
      if (status === undefined) {
        delete next[key];
      } else {
        next[key] = status;
      }
      return next;
    });
  }, [lockedManualKeys]);

  const updateRecordingGradingResult = useCallback((key: string, status: 'correct' | 'incorrect' | undefined) => {
    if (!key) return;
    if (lockedRecordingKeys.has(key)) {
      return;
    }
    setRecordingGradingResults((prev) => {
      const next = { ...prev };
      if (status === undefined) {
        delete next[key];
      } else {
        next[key] = status;
      }
      return next;
    });
  }, [lockedRecordingKeys]);

  const updateWritingAccuracy = useCallback((activityId: string, accuracy: number | null) => {
    if (!activityId) return;
    if (lockedAccuracyKeys.has(activityId)) {
      return;
    }
    setWritingAccuracies((prev) => {
      const prevValue = prev[activityId];
      const isRemoval = accuracy === null || Number.isNaN(accuracy);
      if (isRemoval && prevValue === undefined) {
        return prev;
      }
      if (!isRemoval && prevValue === accuracy) {
        return prev;
      }

      const next = { ...prev };
      if (isRemoval) {
        delete next[activityId];
      } else if (accuracy !== null) {
        next[activityId] = accuracy;
      }
      return next;
    });
  }, [lockedAccuracyKeys]);

  const handleEngWritingAccuracyChange = useCallback((activityId: string, accuracy: number | null) => {
    updateWritingAccuracy(activityId, accuracy);
  }, [updateWritingAccuracy]);

  const updateTeacherComment = useCallback((key: string, text: string) => {
    if (!key) return;
    setTeacherComments((prev) => {
      const prevValue = prev[key];
      if (prevValue === text) return prev;
      const next = { ...prev };
      if (!text) delete next[key];
      else next[key] = text;
      return next;
    });
  }, []);

  const lockCurrentResults = useCallback(() => {
    setLockedManualKeys((prev) => {
      const next = new Set(prev);
      Object.entries(manualGradingResults).forEach(([key, value]) => {
        if (value && value !== 'grading') {
          next.add(key);
        }
      });
      return next;
    });

    setLockedRecordingKeys((prev) => {
      const next = new Set(prev);
      Object.entries(recordingGradingResults).forEach(([key, value]) => {
        if (value) {
          next.add(key);
        }
      });
      return next;
    });

    setLockedAccuracyKeys((prev) => {
      const next = new Set(prev);
      Object.entries(writingAccuracies).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          next.add(key);
        }
      });
      return next;
    });
  }, [manualGradingResults, recordingGradingResults, writingAccuracies]);

  const handleSaveGradingProgress = async () => {
    if (!data || !data.scheduleId || isSaving) return;
    try {
      setIsSaving(true);
      // 채점 데이터 준비
      const gradingData = {
        scheduleId: data.scheduleId,
        totalItems: totalGradingResult.totalItems,
        correctCount: totalGradingResult.correct,
        incorrectCount: totalGradingResult.incorrect,
        gradingCount: totalGradingResult.grading,
        score: totalGradingResult.score,
        recordingGradingResults,
        manualGradingResults,
        writingAccuracies,
        teacherComments,
        status: '채점완료'
      };


      // API 호출
      const response = await academyLearningScheduleApi.completeGrading(data.scheduleId, gradingData);
      
      if (response.success) {
        lockCurrentResults();
        const fullyCompleted = response.data?.fullyCompleted === true;
        setIsGradingCompleted(fullyCompleted);
        if (fullyCompleted) {
          onStatusChange?.('채점완료');
          const emailSent = response.data?.emailSent === true;
        setToast({ 
            message: emailSent
              ? '✅ 채점이 완료되어 성적표를 발송했습니다.'
              : '✅ 채점이 완료되었습니다.',
          status: 'success' 
        });
        } else {
          const remaining = response.data?.remainingCount ?? totalGradingResult.grading;
          if (remaining > 0) {
            setToast({ 
              message: `저장되었습니다. 남은 문항 ${remaining}개를 이어서 채점해주세요.`,
              status: 'success' 
            });
          }
        }
      } else {
        throw new Error(response.message || '채점 완료 처리 실패');
      }
    } catch (error: any) {
      console.error('채점 완료 처리 실패:', error);
      setToast({ 
        message: error.message || '채점 완료 처리 중 오류가 발생했습니다.', 
        status: 'error' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  // 토스트 자동 숨김
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 5000); // 5초 후 자동 숨김
      return () => clearTimeout(timer);
    }
  }, [toast]);
  // 더미 데이터 (data가 없을 때)
  if (!data) {
    return (
        <div ref={scoreContainerRef}>
          <ul className='flex flex-col gap-2'>
              <li className={`${type === 'score' ? 'shadow-[2px_2px_16px_0_rgba(0,0,0,0.08)] rounded-2xl overflow-hidden bg-white' : 'shadow-none rounded-2xl overflow-hidden bg-white'}`}>  
              <div className='flex justify-between items-center px-5 py-3 back_gra_ravendar'>
                  <h2 className='w-full text-lg font-bold'>교제 이름</h2>
              </div>
                  <section>
                       <div className='flex justify-between items-center px-5 py-2 bg-gray-50'>
                       <p className='text-sm'>유닛 이름</p>
                       </div>
                      <section className='flex flex-col gap-2'>
                          <div>
                              <section className='opacity-50 *:p-5 *:border-b *:border-gray-100 last:border-b-0'>
                                  <div className='p-5'>
                                      <p className='text-sm text-gray-500'>활동이 없습니다.</p>
                                  </div>
                              </section>
                          </div>
                      </section>
                  </section>
              </li>
          </ul>
        </div>
    );
  }

  // 실제 데이터
  const textbooks = data.textbooks || [];
  const isScoreView = type === 'score';

  return (
    <div 
        ref={scoreContainerRef}
        onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
                e.preventDefault();
            }
        }}
    >
        <ul className='flex flex-col gap-8'>
            {/* 교제 반복문 */}
            {textbooks.map((textbook: any, tbIdx: number) => {
              const textbookDimmed = isScoreView && !textbook?.completed;
              return (
            <li
              key={tbIdx}
              id={`textbook-${tbIdx}`}
              className={`${type === 'score' ? 'shadow-[2px_2px_16px_0_rgba(0,0,0,0.08)] rounded-2xl overflow-hidden bg-white' : 'shadow-none rounded-2xl overflow-hidden bg-white'} ${textbookDimmed ? 'opacity-60' : 'opacity-100'}`}
            >  
            <div className='flex justify-between items-center px-5 py-3 back_gra_ravendar'>
                <h2 className='w-full text-lg font-bold'>{textbook.textbookTitle}</h2>
                {isScoreView && !textbook.completed && (
                  <span className='flex-none text-xs text-red-500'>아직 풀지 않은 교제 입니다.</span>
                )}
            </div>
                {/* 유닛 반복문  */}
                {textbook.units && textbook.units.map((unit: any, unitIdx: number) => {
                  
                  // 1. 활동 확장 (학습 화면과 동일)
                  const expandedActivities = expandActivities(unit.activities || [], unit.unitType || '');
                  
                  // 2. 활동 순서 정렬
                  const sortedActivities = sortActivities(expandedActivities, unit.unitType || '');
                  
                  // 3. flatItems 생성 (학습 화면과 동일한 구조)
                  const flatItems = createFlatItems(sortedActivities);
                  
                  // responseJson 파싱
                  let responses: any = {};
                  if (unit.responseJson) {
                    try {
                      responses = JSON.parse(unit.responseJson);
                    } catch (e) {
                      console.error('responseJson 파싱 실패:', e);
                    }
                  }
                  
                  return (
                <section key={unitIdx} className={`${isScoreView && !unit?.completed ? 'opacity-50' : 'opacity-100'}`}>
                     <div className='flex justify-between items-center px-5 py-3 bg-gradient-to-r from-blue-50 to-purple-50 border-l-4 border-blue-500'>
                     <p className='text-base font-bold text-gray-800'>{unit.unitTitle}</p>
                     {!unit.completed && (
                        <span className='flex-none text-xs font-semibold text-red-500'>아직 풀지 않은 유닛 입니다.</span>
                     )}
                     </div>
                    {/* 문제 반복문 */}
                    <section className='flex flex-col gap-2'>
                        <div>
                            {/* 풀지 않은 문제인 경우 반 투명하게 처리 */}
                            <section className={`${unit.completed ? 'opacity-100' : 'opacity-50'} *:p-5 *:border-b *:border-gray-100 last:border-b-0`}>
                                {flatItems.length > 0 ? (
                                  (() => {
                                    // 동일 유닛 내 같은 유형의 제목 중복 방지용 플래그
                                    let dictationTitlePrinted = false;
                                    let spellCheckTitlePrinted = false;
                                    let alphabetChoiceTitlePrinted = false;
                                    return flatItems.map((flatItem: any, flatIdx: number) => {
                                    const activity = flatItem.activity;
                                    if ((type === 'grading' && activity.activityType === 'VIDEO_QUIZ') || (hideVideoQuiz && activity.activityType === 'VIDEO_QUIZ')) {
                                      return null;
                                    }
                                    const activityResponse = responses[flatItem.globalIndex] || {};
                                    const activityResponseJson = JSON.stringify(activityResponse);
                                    
                                  
                                  // 활동 타입에 따라 적절한 컴포넌트 렌더링
                                  switch(activity.activityType) {
                                    case 'VIDEO_QUIZ':
                                      if (suppressVideoQuizUi) {
                                        return null;
                                      }
                                      return <GradingVideoQuiz key={`${flatItem.globalIndex}-${activity.id}`} type={type === 'grading' ? 'gradingCard' : 'scoreCard'} activity={activity} responseJson={activityResponseJson} />;
                                    case 'FLASH_CARD': {
                                      // 같은 FLASH_CARD 활동은 하나의 카드로 묶어서 표시 (첫 아이템에서만 렌더)
                                      if (flatItem.itemIndex !== 0) return null;
                                      const aggregatedResponses: Record<string, any> = {};
                                      if (activity.items && activity.items.length > 0) {
                                        activity.items.forEach((_: any, itemIdx: number) => {
                                          const target = flatItems.find((fi: any) => fi.activity && fi.activity.id === activity.id && fi.itemIndex === itemIdx);
                                          if (target) {
                                            aggregatedResponses[String(itemIdx)] = responses[target.globalIndex] || {};
                                          }
                                        });
                                      }
                                        const aggregatedJson = JSON.stringify(aggregatedResponses);
                                        return <GradingFlashCard 
                                          key={`flashcard-${activity.id}`} 
                                          type={type === 'grading' ? 'gradingCard' : 'scoreCard'} 
                                          activity={activity} 
                                          responseJson={aggregatedJson}
                                          canUseAi={canUseAi}
                                          recordingGradingResults={recordingGradingResults}
                                          lockedRecordingKeys={lockedRecordingKeys}
                                          onRecordingGradingChange={(uniqueKey, status) => {
                                            // uniqueKey는 이미 "activityId-itemId" 형식
                                            const itemKey = `flashcard-${uniqueKey}`;
                                            updateRecordingGradingResult(itemKey, status);
                                          }}
                                        />;
                                    }
                                        case 'DICTATION': {
                                          // 같은 유닛 내 DICTATION 제목은 최초 1회만 노출
                                          const showTitle = !dictationTitlePrinted;
                                          dictationTitlePrinted = true;
                                          // originalItemId를 키로 사용 (Math.random()으로 생성된 id는 매번 바뀜)
                                          const dictationKey = `dictation-${activity.originalItemId || activity.items?.[0]?.id || activity.id}`;
                                          const dictationLocked = isRecordingLocked(dictationKey);
                                          return <GradingDictation 
                                            key={dictationKey} 
                                            type={type === 'grading' ? 'gradingCard' : 'scoreCard'} 
                                            activity={activity} 
                                            responseJson={activityResponseJson} 
                                            showTitle={showTitle}
                                            canUseAi={canUseAi && !dictationLocked}
                                            recordingGradingResult={recordingGradingResults[dictationKey]}
                                            recordingLocked={dictationLocked}
                                            onRecordingGradingChange={(status) => {
                                              updateRecordingGradingResult(dictationKey, status);
                                            }}
                                          />;
                                        }
                                    case 'DRAG_MATCH':
                                      return <GradingDragMatch key={`${flatItem.globalIndex}-${activity.id}`} activity={activity} responseJson={activityResponseJson} />;
                                        case 'SPELL_CHECK': {
                                          // 같은 유닛 내 SPELL_CHECK 제목은 최초 1회만 노출
                                          const showTitle = !spellCheckTitlePrinted;
                                          spellCheckTitlePrinted = true;
                                          return <GradingSpellCheck key={`${flatItem.globalIndex}-${activity.id}`} activity={activity} responseJson={activityResponseJson} showTitle={showTitle} />;
                                        }
                                        case 'ALPHABET_CHOICE': {
                                          // 같은 유닛 내 ALPHABET_CHOICE 제목은 최초 1회만 노출
                                          const showTitle = !alphabetChoiceTitlePrinted;
                                          alphabetChoiceTitlePrinted = true;
                                          return <GradingAlphabetChoice key={`${flatItem.globalIndex}-${activity.id}`} type={type === 'grading' ? 'gradingCard' : 'scoreCard'} activity={activity} responseJson={activityResponseJson} showTitle={showTitle} />;
                                        }
                                    case 'WORD_CHOICE':
                                      {
                                      const recordingKey = buildWordChoiceRecordingKey(
                                        activity.id,
                                        flatItem.item?.id,
                                        flatItem.itemIndex,
                                      );
                                      const legacyRecordingKey = `wc-${activity.id}`;
                                      const wordChoiceItemCount = Array.isArray(activity.items) ? activity.items.length : 0;
                                      const recordingResult = recordingGradingResults[recordingKey]
                                        ?? (wordChoiceItemCount <= 1 ? recordingGradingResults[legacyRecordingKey] : undefined);
                                      const manualKey = `${activity.id}-0`;
                                      const recordingLocked = isRecordingLocked(recordingKey);
                                      const manualLocked = isManualLocked(manualKey);
                                      return <GradingWordChoice 
                                        key={`${flatItem.globalIndex}-${activity.id}`} 
                                        type={type === 'grading' ? 'gradingCard' : 'scoreCard'} 
                                        activity={activity} 
                                        item={flatItem.item}
                                        responseJson={activityResponseJson}
                                        unitType={unit.unitType}
                                        canUseAi={canUseAi && !recordingLocked}
                                        recordingGradingResult={recordingResult}
                                        recordingLocked={recordingLocked}
                                        onRecordingGradingChange={(status) => {
                                          updateRecordingGradingResult(recordingKey, status);
                                        }}
                                        manualGradingResult={manualGradingResults[manualKey]}
                                        manualLocked={manualLocked}
                                        onManualGradingChange={(status) => {
                                          updateManualGradingResult(manualKey, status);
                                        }}
                                      />;
                                      }
                                    case 'DIAGRAM':
                                      return <GradingDiagram key={`${flatItem.globalIndex}-${activity.id}`} activity={activity} responseJson={activityResponseJson} />;
                                    case 'PART_WRITING': {
                                      // Nuance 확장(WORD_CHOICE → PART_WRITING) 시 id가 매 렌더마다 달라지는 문제를 회피하기 위해 안정 키 사용
                                      const stableBaseId = (activity as any).originalType === 'WORD_CHOICE'
                                        ? (activity.items?.[0]?.refId ?? activity.id)
                                        : activity.id;
                                      const partWritingKey = `partwriting-${stableBaseId}`;
                                      const manualKey = `pw-${stableBaseId}`;
                                      const recordingLocked = isRecordingLocked(partWritingKey);
                                      const manualLocked = isManualLocked(manualKey);
                                      return <GradingPartWriting 
                                        key={`${flatItem.globalIndex}-${activity.id}`} 
                                        type={type === 'grading' ? 'gradingCard' : 'scoreCard'} 
                                        activity={activity} 
                                        responseJson={activityResponseJson}
                                        canUseAi={canUseAi && !recordingLocked}
                                        recordingGradingResult={recordingGradingResults[partWritingKey]}
                                        recordingLocked={recordingLocked}
                                        manualLocked={manualLocked}
                                        onRecordingGradingChange={(status) => {
                                          updateRecordingGradingResult(partWritingKey, status);
                                        }}
                                        onManualGradingChange={(status) => {
                                          updateManualGradingResult(manualKey, status);
                                        }}
                                         manualGradingResult={manualGradingResults[manualKey]}
                                      />;
                                    }
                                    case 'FILL_BLANK':
                                      return <GradingFillBlank key={`${flatItem.globalIndex}-${activity.id}`} type={type === 'grading' ? 'gradingCard' : 'scoreCard'} activity={activity} responseJson={activityResponseJson} />;
                                    case 'ENG_WRITING':
                                      return <GradingEngWritng 
                                        key={`${flatItem.globalIndex}-${activity.id}`} 
                                        type={type === 'grading' ? 'gradingCard' : 'scoreCard'} 
                                        activity={activity} 
                                        responseJson={activityResponseJson}
                                        manualGradingResult={manualGradingResults[`${activity.id}`]}
                                        onManualGradingChange={(status) => {
                                          updateManualGradingResult(`${activity.id}`, status);
                                        }}
                                        onAccuracyChange={handleEngWritingAccuracyChange}
                                      />;
                                    case 'FREE_WRITING': {
                                      // 라인별 상태를 개별 키로 관리: fw-{activity.id}-{lineIdx}
                                      const lineResults: Record<number, 'correct' | 'incorrect' | 'grading'> = {};
                                      Object.keys(manualGradingResults).forEach(key => {
                                        if (key.startsWith(`fw-${activity.id}-`)) {
                                          const lineIdx = parseInt(key.split('-')[2]);
                                          lineResults[lineIdx] = manualGradingResults[key];
                                        }
                                      });

                                      const lineComments: Record<number, string> = {};
                                      Object.keys(teacherComments).forEach(key => {
                                        if (key.startsWith(`fw-${activity.id}-`)) {
                                          const lineIdx = parseInt(key.split('-')[2]);
                                          lineComments[lineIdx] = teacherComments[key];
                                        }
                                      });
                                      
                                      return <GradingFreeWritng 
                                        key={`${flatItem.globalIndex}-${activity.id}`} 
                                        type={type === 'grading' ? 'gradingCard' : 'scoreCard'} 
                                        activity={activity} 
                                        responseJson={activityResponseJson}
                                        canUseAi={canUseAi}
                                        manualGradingResults={lineResults}
                                        lockedManualKeys={lockedManualKeys}
                                        onManualGradingChange={(lineIdx, status) => {
                                          updateManualGradingResult(`fw-${activity.id}-${lineIdx}`, status);
                                        }}
                                        teacherComments={lineComments}
                                        onTeacherCommentChange={(lineIdx, text) => {
                                          updateTeacherComment(`fw-${activity.id}-${lineIdx}`, text);
                                        }}
                                      />;
                                    }
                                    case 'STORY_DIALOG': {
                                      // 문장별 상태를 개별 키로 관리: sd-{activity.id}-{sentenceIdx}
                                      const sentenceResults: Record<number, 'correct' | 'incorrect' | 'grading'> = {};
                                      Object.keys(manualGradingResults).forEach(key => {
                                        if (key.startsWith(`sd-${activity.id}-`) || key.startsWith(`${activity.id}-sentence-`)) {
                                          const parts = key.split('-');
                                          const sentenceIdx = parseInt(parts[parts.length - 1]);
                                          sentenceResults[sentenceIdx] = manualGradingResults[key];
                                        }
                                      });
                                      
                                      return <GradingStoryDialog 
                                        key={`${flatItem.globalIndex}-${activity.id}`} 
                                        type={type === 'grading' ? 'gradingCard' : 'scoreCard'} 
                                        activity={activity} 
                                        responseJson={activityResponseJson}
                                        canUseAi={canUseAi}
                                        manualGradingResults={sentenceResults}
                                        lockedManualKeys={lockedManualKeys}
                                        onManualGradingChange={(sentenceIdx, status) => {
                                          updateManualGradingResult(`${activity.id}-sentence-${sentenceIdx}`, status);
                                        }}
                                      />;
                                    }
                                    case 'STORY_DIALOG_QA': {
                                      // 질문별 상태를 개별 키로 관리: sdq-{activity.id}-{questionIdx}
                                      const questionResults: Record<number, 'correct' | 'incorrect' | 'grading'> = {};
                                      Object.keys(manualGradingResults).forEach(key => {
                                        if (key.startsWith(`sdq-${activity.id}-`) || key.startsWith(`${activity.id}-question-`)) {
                                          const parts = key.split('-');
                                          const questionIdx = parseInt(parts[parts.length - 1]);
                                          questionResults[questionIdx] = manualGradingResults[key];
                                        }
                                      });
                                      
                                      return <GradingStoryDialogQustion 
                                        key={`${flatItem.globalIndex}-${activity.id}`} 
                                        type={type === 'grading' ? 'gradingCard' : 'scoreCard'} 
                                        activity={activity} 
                                        responseJson={activityResponseJson}
                                        canUseAi={canUseAi}
                                        manualGradingResults={questionResults}
                                        lockedManualKeys={lockedManualKeys}
                                        onManualGradingChange={(questionIdx, status) => {
                                          updateManualGradingResult(`${activity.id}-question-${questionIdx}`, status);
                                        }}
                                      />;
                                    }
                                    default:
                                      return (
                                        <div key={`${flatItem.globalIndex}-${activity.id}`} className='p-5'>
                                          <p className='text-sm text-gray-500'>지원하지 않는 활동 유형: {activity.activityType}</p>
                                        </div>
                                      );
                                  }
                                    });
                                  })()
                                ) : (
                                  <div className='p-5'>
                                    <p className='text-sm text-gray-500'>활동이 없습니다.</p>
                                  </div>
                                )}
                    </section>
                        </div>
                    </section>
                </section>
                  );
                })}
            </li>
              );
            })}
        </ul>
        
        {/* 저장 버튼 */}
        {type === 'grading' && !isGradingCompleted && (
          <div className='flex justify-center my-6'>
            <Button size='large' onClick={handleSaveGradingProgress} disabled={isSaving}>
              {isSaving ? '저장 중...' : '확인'}
            </Button>
          </div>
        )}
        
        {/* 채점 완료됨 표시 */}
        {isGradingCompleted && (
          <div className='p-4 mt-6 text-center bg-green-50 rounded-lg border border-green-200'>
            <p className='text-lg font-bold text-green-700'>✓ 채점이 완료되었습니다.</p>
          </div>
        )}

        {/* 토스트 메시지 */}
        {toast && (
          <Toast
            message={toast.message}
            status={toast.status}
            isVisible={!!toast}
          />
        )}
        {/* 저장 프리로딩 */}
        {type === 'grading' && isSaving && (
          <div className='fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-40'>
            <div className='flex flex-col items-center gap-3 px-8 py-6 bg-white rounded-2xl shadow-2xl'>
              <div className='w-12 h-12 rounded-full border-4 border-indigo-200 border-t-indigo-600 animate-spin' />
              <div className='text-center'>
                <p className='text-lg font-semibold text-gray-900'>채점 결과를 저장하고 있어요</p>
                <p className='text-sm text-gray-500 mt-1'>잠시만 기다려주세요...</p>
              </div>
            </div>
          </div>
        )}
      </div>
  )
})

export default HomeworkScore   
